/**
 * Peer `pwa_action` sockets (DEC-269, 2026-10-11): listen for `pwa_action` on
 * every paired bridge that is NOT the primary.
 *
 * Why it exists. A timeline plays on its HOME bridge (DEC-224), often the Mac
 * Studio while this device's primary is another machine. Its light cues come
 * back as `pwa_action` frames only on sockets connected to THAT bridge:
 * OPBridge has no peer relay (opbridge-96, 2026-10-11: the EventBus is
 * in-process and `peers_ws.rs` never touches it). `consumerWs.ts` follows the
 * primary only, so without this the canvas would light only for timelines
 * that happen to play on the primary.
 *
 * Deliberately minimal. One socket per peer, `pwa_action` frames (plus a
 * timeline's `timeline_fired`, DEC-269 Phase B, OPVisual's status, DEC-272, and
 * a controller's `control`, DEC-256),
 * fed into the primary socket's own listeners through `emitPwaAction` /
 * `emitTimelineFired`. Everything
 * else a consumer socket carries (inheritance, revocation, docs, setlist,
 * `midi_in`, performance mode) stays the primary socket's alone, because a
 * second copy of any of those would double-deliver. No HTTP probe either: a
 * peer whose bearer was revoked just fails its upgrade and backs off to the
 * cap, quietly; the primary socket owns revocation handling.
 *
 * Adapts DEC-063's "WS subscriptions are primary-only" (see `storage.ts`
 * `is_primary`) for this one event type. Peers were already reached directly
 * before this: the timeline state poll reads them over HTTP (DEC-224) and the
 * Traffic Monitor opens sockets to them.
 *
 * Uses the stored `baseUrl`, exactly as `consumerWs.ts` does for the primary,
 * so both sockets reach a bridge the same way.
 */

import { createRoot, createEffect, createMemo, on } from 'solid-js';
import { bridges, primaryBridge } from './store.ts';
import type { BridgeConfig } from './storage.ts';
import { OPBRIDGE_SUBPROTOCOL } from './opbridgeConfig.ts';
import { emitPwaAction, emitTimelineFired, parseTimelineFired, emitControlEvent, parseControlEvent, emitFired, parseFiredEvent } from './consumerWs.ts';
import { ingestOpvStatus } from '../opvisual/live.ts';

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;
/** A socket that stayed up this long resets the backoff. */
const STABLE_MS = 5_000;

interface Channel {
  key: string;
  cfg: BridgeConfig;
  socket: WebSocket | null;
  attempt: number;
  timer: ReturnType<typeof setTimeout> | null;
  closed: boolean;
}

const channels = new Map<string, Channel>();   // bridge row id -> channel
let disposeRoot: (() => void) | null = null;

function keyOf(b: BridgeConfig): string {
  return `${b.id} ${b.bearer ?? ''} ${b.baseUrl}`;
}

/** Paired bridges other than the primary: the ones this module listens to. */
function peers(): BridgeConfig[] {
  const primaryId = primaryBridge()?.id;
  return bridges().filter((b) => !!b.bearer && b.id !== primaryId);
}

function open(ch: Channel): void {
  if (ch.closed || !ch.cfg.bearer) return;
  const url = `${ch.cfg.baseUrl.replace(/\/+$/, '').replace(/^http/i, 'ws')}/ws/consumer`;
  let sock: WebSocket;
  try {
    sock = new WebSocket(url, [OPBRIDGE_SUBPROTOCOL, ch.cfg.bearer]);
  } catch {
    retry(ch);
    return;
  }
  ch.socket = sock;
  let openedAt = 0;
  sock.addEventListener('open', () => { openedAt = performance.now(); });
  sock.addEventListener('message', (ev) => {
    if (ch.socket !== sock) return;
    let msg: Record<string, unknown>;
    try {
      const parsed = JSON.parse(String(ev.data)) as unknown;
      if (!parsed || typeof parsed !== 'object') return;
      msg = parsed as Record<string, unknown>;
    } catch {
      return;
    }
    // OPVisual's status frames (DEC-272): OPVisual is often attached to a bridge
    // that is not this device's primary, so its frames arrive only here.
    if (msg['event'] === 'opvisual_status') { ingestOpvStatus(ch.cfg.id, msg); return; }
    // A timeline playing on this bridge reports each step it fired (DEC-269 Phase B).
    if (msg['event'] === 'timeline_fired') {
      const evt = parseTimelineFired(msg, ch.cfg.id);
      if (evt) emitTimelineFired(evt);
      return;
    }
    // A controller press (DEC-256's "press to pick"). Presses cross the mesh,
    // so this duplicates the primary's copy; the one listener dedupes.
    if (msg['event'] === 'control') {
      const evt = parseControlEvent(msg, ch.cfg.id);
      if (evt) emitControlEvent(evt);
      return;
    }
    // One of our rules fired, or one of our named actions was pressed, on THIS bridge (S324): not relayed, so only here.
    if (msg['event'] === 'rule_fired' || msg['event'] === 'action_fired') {
      const evt = parseFiredEvent(msg, ch.cfg.id);
      if (evt) emitFired(evt);
      return;
    }
    if (msg['event'] !== 'pwa_action') return;
    const command = msg['command'];
    if (typeof command !== 'string') return;
    const ruleId = msg['rule_id'];
    emitPwaAction({ ruleId: typeof ruleId === 'string' ? ruleId : '', command, args: msg['args'], bridgeId: ch.cfg.id });
  });
  sock.addEventListener('close', () => {
    if (ch.socket !== sock) return;
    ch.socket = null;
    if (openedAt > 0 && performance.now() - openedAt > STABLE_MS) ch.attempt = 0;
    retry(ch);
  });
  sock.addEventListener('error', () => { /* `close` follows and drives the retry */ });
}

function retry(ch: Channel): void {
  if (ch.closed || ch.timer !== null) return;
  const delay = Math.min(RECONNECT_BASE_MS * Math.pow(1.6, ch.attempt), RECONNECT_CAP_MS);
  ch.attempt += 1;
  ch.timer = setTimeout(() => { ch.timer = null; open(ch); }, delay * (1 + Math.random() * 0.3));
}

function close(ch: Channel): void {
  ch.closed = true;
  if (ch.timer !== null) { clearTimeout(ch.timer); ch.timer = null; }
  const s = ch.socket;
  ch.socket = null;
  if (s) { try { s.close(); } catch { /* */ } }
}

function reconcile(): void {
  const want = new Map(peers().map((b) => [b.id, b] as const));
  for (const [id, ch] of channels) {
    const b = want.get(id);
    if (!b || keyOf(b) !== ch.key) { close(ch); channels.delete(id); }
  }
  for (const [id, b] of want) {
    if (channels.has(id)) continue;
    const ch: Channel = { key: keyOf(b), cfg: b, socket: null, attempt: 0, timer: null, closed: false };
    channels.set(id, ch);
    open(ch);
  }
}

/** Start listening to every paired peer. Idempotent. Re-reconciles only when
 *  the set of peers (id, bearer, URL) actually changes, so an unrelated
 *  bridge edit does not churn the sockets. */
export function startPeerPwaSockets(): void {
  if (disposeRoot) return;
  createRoot((dispose) => {
    disposeRoot = dispose;
    const want = createMemo(() => peers().map(keyOf).sort().join('|'));
    createEffect(on(want, () => reconcile()));
  });
}

export function stopPeerPwaSockets(): void {
  disposeRoot?.();
  disposeRoot = null;
  for (const ch of channels.values()) close(ch);
  channels.clear();
}

/** Bridge ids with an OPEN peer socket - for diagnostics and smokes. */
export function peerPwaSocketsOpen(): string[] {
  return [...channels.values()].filter((c) => c.socket?.readyState === WebSocket.OPEN).map((c) => c.cfg.id);
}
